import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  EmptyState,
  Pagination,
  SurfaceLoadError,
  SurfaceLoading,
  SurfaceStaleHint,
} from '@/shared/components';
import { problemText, useApi, useApiProblem, type ChannelCloseoutListItem } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { ChannelCloseoutRow, type CloseoutAction } from './ChannelCloseoutRow';
import { CloseoutsOutsideViewNote } from './CloseoutsOutsideViewNote';
import { refetchWhileUnconfirmed } from './useCloseoutTimeline';

type CloseoutFilter = 'active' | 'archived' | 'all';

const FILTERS: [CloseoutFilter, string][] = [
  ['active', 'Aktiv'],
  ['archived', 'Archiv'],
  ['all', 'Alle'],
];

const EMPTY_TITLE: Record<CloseoutFilter, string> = {
  active: 'Keine aktiven Kanalregeln',
  archived: 'Keine aufgehobenen Kanalregeln',
  all: 'Noch keine Kanalregeln',
};

export function ChannelCloseoutList({
  propertyGroupId,
  canManage,
  busy,
  onEdit,
  onShowInCalendar,
  onAction,
}: {
  propertyGroupId: string;
  canManage: boolean;
  busy: boolean;
  onEdit: (closeoutId: string) => void;
  onShowInCalendar: (startDate: string) => void;
  onAction: (item: ChannelCloseoutListItem, action: CloseoutAction) => void;
}) {
  const api = useApi();
  const [filter, setFilter] = useState<CloseoutFilter>('active');
  const [page, setPage] = useState(1);
  const closeouts = useQuery({
    queryKey: ['availability-distribution', 'closeouts', propertyGroupId, page, filter],
    queryFn: () => api.availabilityDistribution.closeouts.list(propertyGroupId, page, filter),
    placeholderData: (previous) => previous,
    refetchInterval: (query) => refetchWhileUnconfirmed(query.state.data?.items),
  });
  const items = closeouts.data?.items ?? [];
  const problem = useApiProblem(closeouts.error);
  const retry = () => void closeouts.refetch();
  return (
    <div className="space-y-3">
      <div
        role="radiogroup"
        aria-label="Regeln filtern"
        className="bg-card-alt rounded-pill inline-flex gap-0.5 p-1"
      >
        {FILTERS.map(([value, label]) => {
          const active = filter === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => {
                setFilter(value);
                setPage(1);
              }}
              className={cn(
                'rounded-pill h-11 px-3.5 text-[13px]',
                active ? 'text-ink bg-white font-bold' : 'text-ink-soft font-semibold',
              )}
            >
              {active && closeouts.data ? `${label} · ${closeouts.data.total}` : label}
            </button>
          );
        })}
      </div>
      {/* Scheitert nur das Nachladen, bleibt die Liste stehen und sagt, welcher Stand das ist. */}
      {closeouts.data && closeouts.isError && (
        <SurfaceStaleHint onRetry={retry} retrying={closeouts.isFetching} />
      )}
      {/* Nur „Aktiv“ zählt der Server; ein Platzhalter der vorigen Auswahl sagte Falsches. */}
      {filter === 'active' && !closeouts.isPlaceholderData && (
        <CloseoutsOutsideViewNote count={closeouts.data?.outsideViewCount} />
      )}
      {!closeouts.data && closeouts.isError ? (
        <SurfaceLoadError
          message={problemText(problem, 'Die Kanalregeln konnten nicht geladen werden.')}
          onRetry={retry}
          retrying={closeouts.isFetching}
        />
      ) : !closeouts.data ? (
        <SurfaceLoading label="Kanalregeln werden geladen" rows={3} />
      ) : items.length === 0 ? (
        <EmptyState
          icon="CalendarDays"
          title={
            filter === 'active' && closeouts.data.outsideViewCount > 0
              ? 'Keine Kanalregel nur für deine Apartments'
              : EMPTY_TITLE[filter]
          }
          description="Eine Kanalregel schließt Booking.com oder Airbnb, ohne Preise und Mindestnächte zu verändern."
        />
      ) : (
        <>
          <div className="flex flex-col gap-2.5">
            {items.map((item) => (
              <ChannelCloseoutRow
                key={item.id}
                item={item}
                canManage={canManage}
                busy={busy}
                onEdit={() => onEdit(item.id)}
                onShowInCalendar={() => onShowInCalendar(item.startDate)}
                onAction={(action) => onAction(item, action)}
              />
            ))}
          </div>
          {closeouts.data && (
            <Pagination
              page={page}
              pageSize={closeouts.data.pageSize}
              total={closeouts.data.total}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </div>
  );
}
